import loadable from "@loadable/component"
import { useEffect, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { useSelector } from "react-redux"

import useCollection from "areas/collection/hoc/use-collection"
import IntersectionWrapper from "components/common/intersection-wrapper"
import Perks from "components/perks"
import Divider from "components/ui/divider"
import Media from "components/ui/media"
// import Picture from "components/ui/picture"
import { submit_ga_tracker } from "helpers/analytics-helpers"
import { IS_JEWLR, isTestMode, retry } from "helpers/application"
import { logCurrentPageInCollectionStats } from "helpers/collection"
// import { getImgSrcSet } from "helpers/image"
import useScrollRestoration from "helpers/use-scroll-restoration"
import fetchWishlist from "hoc/fetch-wishlist"
import { cn } from "styles/utils"

import Banner from "./banner"
import FilterAccordion from "./filter-accordion"
import FilterHeader from "./filter-header"
import Heading from "./heading"
import LoadMoreProducts from "./load-more-products"
import FilterAccordionLoading from "./loaders/filter-accordion-loading"
import Products from "./products"
import SortMenu from "./sort-menu"
import SuperFilters from "./super-filters"

const CollectionDescription = loadable(
  () => retry(() => import("./collection-description")),
  { ssr: false }
)
const Slider = loadable(() => retry(() => import("./slider")), { ssr: false })
const FilterMobile = loadable(() => retry(() => import("./filter-mobile")), {
  ssr: false,
})
const FilterTags = loadable(() => retry(() => import("./filter-tags")), {
  ssr: false,
})

// const BFCMBanner = {
//   alt: "Biggest Sale of the Season. Limited Time Only!",
//   srcSet: [
//     {
//       height: 200,
//       media: "(max-width: 767px)",
//       sizes: "100vw",
//       srcSet: getImgSrcSet(
//         "https://cms.jewlr.com/m_biggest_sale_coll_sml_5dad0d1c94/m_biggest_sale_coll_sml_5dad0d1c94.jpg",
//         750
//       ),
//       width: 750,
//     },
//     {
//       height: 564,
//       media: "(min-width: 768px)",
//       sizes: "100vw",
//       srcSet: getImgSrcSet(
//         "https://cms.jewlr.com/d_cyber_week_coll_biggest_sale_96d722b4d9/d_cyber_week_coll_biggest_sale_96d722b4d9.jpg",
//         3840
//       ),
//       width: 3840,
//     },
//   ],
// }

const Collection = () => {
  const productAreaRef = useRef()
  const newPageFocusRef = useRef()
  const loading = useSelector((state) => state.collection.loading)

  const { data, hasNextPage } = useCollection()
  const mainBanners = data?.banners?.filter((b) => b.location === "main") || []
  const noProducts = data?.product_result?.length === 0
  const isSearch = data?.mountedPath === "/search"
  const showFilters = IS_JEWLR() && (!isSearch || !noProducts)

  useScrollRestoration()

  useEffect(() => {
    if (data && !data.redirect_url) {
      logCurrentPageInCollectionStats(data?.mountedPath)

      if (data.unique_event_id) {
        window.cache_free_ssr_data.unique_event_ids.collection =
          data.unique_event_id
      }

      if (!isTestMode) {
        let lastViewedItem = null
        const firstItem = data?.product_result?.[0]

        if (firstItem && firstItem.looks) {
          // For Iterable session abandonment workflow, we show the first
          // product of the collection as the last viewed item for dynamic
          // product block on emails

          const firstItemLook = firstItem.default_look
            ? firstItem.looks[firstItem.default_look]
            : Object.values(firstItem.looks)?.[0]

          if (firstItemLook) {
            const productImage = firstItemLook.images?.[0]
              ?.split(",")
              ?.map((srcset) => srcset.trim())
              ?.slice(-1)?.[0]
              ?.split(" ")?.[0]

            if (productImage) {
              lastViewedItem = {
                productImage,
                productPrice: firstItemLook.selling,
                productTitle: firstItem.name,
                productUrl: `https://${sl.config.site_url}${firstItemLook.product_url}`,
                styleCode: firstItem.rm_style_code,
              }
            }
          }
        }

        dataLayer.push({
          collectionTitle: data?.title,
          contentIds: data?.product_result
            ?.slice(0, 9)
            ?.map((p) => p.rm_style_code),
          contentName: data?.mountedPath,
          contentType: "product",
          event: "view-collection",
          viewCollectionUniqueEventId:
            window.cache_free_ssr_data.unique_event_ids.collection,
          ...(lastViewedItem && { lastViewedItem }),
        })

        if (data.product_result?.length > 0) {
          dataLayer.push({
            event: "google-remarketing",
            googleRemarketing: {
              ecomm_category: isSearch
                ? null
                : data.filter_row.clear_all_link.replace("/", ""),
              ecomm_pagetype: isSearch ? "searchresults" : "category",
              ecomm_prodid: data.product_result.map((p) => p.style_code),
              ecomm_totalvalue: data.product_result.reduce((total, p) => {
                if (p.looks && p.default_look && p.looks[p.default_look]) {
                  return total + (p.looks[p.default_look].selling || 0)
                } else {
                  return total
                }
              }, 0),
            },
          })
        }

        if (isSearch) {
          dataLayer.push({
            contentIds: data.product_result
              .slice(0, 9)
              .map((p) => p.rm_style_code),
            contentType: "product",
            event: "search",
            searchString: data.preserve_cat_path,
          })
          submit_ga_tracker(`/search?k=${data.preserve_cat_path}`)
        } else if (data.pagination?.current_page) {
          submit_ga_tracker(`/${data.pagination.current_page}`)
        }
      }
    }
  }, [])

  return (
    <>
      <Helmet encodeSpecialCharacters={false}>
        {/* eslint-disable react/jsx-sort-props, react/no-unknown-property */}
        {data?.default_collection_image_url && (
          <meta
            content={data.default_collection_image_url}
            name="og_image"
            property="og:image"
          />
        )}
        {/* {isBFCM && BFCMBanner
          ? BFCMBanner.srcSet.map((srcset, i) => (
              <link
                key={`bfcm-banner-preload-${i}-${srcset.media}`}
                rel="preload"
                fetchPriority="high"
                as="image"
                imageSrcSet={srcset.srcSet}
                imageSizes={srcset.sizes}
                media={srcset.media}
              />
            )) */}
        {mainBanners.length > 0 &&
          (mainBanners[0].image?.srcSet || []).map((srcset, i) => (
            <link
              key={`collection-banner-preload-${
                mainBanners[0].image?.id || i
              }-${srcset.media}`}
              rel="preload"
              fetchPriority="high"
              as="image"
              imageSrcSet={srcset.srcSet}
              imageSizes={srcset.sizes}
              media={srcset.media}
            />
          ))}
        {/* eslint-enable react/jsx-sort-props, react/no-unknown-property */}
      </Helmet>
      {/* {isBFCM && BFCMBanner ? (
        <div data-optimize="collection-banner">
          <Picture alt={BFCMBanner.alt} srcSet={BFCMBanner.srcSet} />
        </div>
      ) :  */}
      {mainBanners.length > 0 && (
        <div data-optimize="collection-banner">
          <Banner firstElement lazyload={false} medias={mainBanners} />
        </div>
      )}
      <div className="contained-wide px-16">
        <Heading />
        {showFilters && (
          <Media lessThan="desktop">
            <SuperFilters />
            <FilterTags />
          </Media>
        )}
        <Divider
          className={cn("mb-10", !noProducts && "desktop:block hidden")}
        />
        <div id="collection-scroll-back-point">
          {showFilters && (
            <Media lessThan="desktop">
              <FilterHeader />
            </Media>
          )}
          <div className="flex justify-center gap-32">
            {showFilters && (
              <Media className="max-w-215 w-1/4" greaterThanOrEqual="desktop">
                {loading === "full" ? (
                  <FilterAccordionLoading />
                ) : (
                  <nav aria-label="Filters">
                    <FilterAccordion />
                  </nav>
                )}
              </Media>
            )}

            <div
              className={cn(
                "min-w-0 flex-1",
                IS_JEWLR() && "desktop:pt-0 pt-24"
              )}
            >
              {!noProducts && (
                <Media lessThan="desktop">
                  <FilterMobile />
                </Media>
              )}
              {showFilters && (
                <Media greaterThanOrEqual="desktop">
                  <SuperFilters />
                  <Divider className="mt-10" />
                  <div className="flex items-center justify-end">
                    <FilterTags />
                    <SortMenu />
                  </div>
                  <Divider className="mb-10" />
                </Media>
              )}
              <section aria-atomic aria-live="polite" ref={productAreaRef}>
                <Products newPageFocusRef={newPageFocusRef} />
              </section>
            </div>
          </div>

          <div className="mb-110 mt-32" data-cy="collection-load-product">
            {hasNextPage && (
              <LoadMoreProducts newPageFocusRef={newPageFocusRef} />
            )}
          </div>
        </div>
      </div>
      {IS_JEWLR() && (
        <IntersectionWrapper>
          <Slider />
          <Perks />
          <CollectionDescription />
        </IntersectionWrapper>
      )}
    </>
  )
}

export default fetchWishlist(Collection)
